import { assertConstantValue } from '@watershed/lscript/assert';
import { ASTCall } from '@watershed/lscript/ast';
import { ExprType } from '@watershed/lscript/expr_types';
import { singleQuote } from '@watershed/lscript/sql';

import { joinTickedWithAnd } from '../../utils/helpers';
import { extractStringLiteral } from '../literal';
import { addInputVariable, traceHelper } from '../sql/internal';
import { TraceASTInput, TraceASTType } from '../trace';
import {
  assertArgShape,
  assertKwargShape,
  TypecheckWarning,
  typecheckExpression,
} from '../typecheck';
import { Context, Func, SqlExpression } from '../types';

// Function for looking up a column in BART row. The syntax is:
// bart([column name])
export const bart: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): ExprType {
    assertArgShape(call, 1, 1);
    assertKwargShape(call, [], false, warnings, undefined);

    // It's important that it's a literal, since we use static analysis to
    // figure out which BART columns a given expression uses.
    const field = extractStringLiteral(call.args[0]);

    if (field === 'month') {
      // The extBART schema should guarantee this type.
      return { ty: 'date' };
    }

    if (field.match(/^tags\./)) {
      // Tags can only be strings, but any value is valid
      return { ty: 'string' };
    }

    if (!ctx.inputType.has(field)) {
      const known = joinTickedWithAnd(ctx.inputType.keys());
      warnings?.push(
        `Unknown BART column \`${field}\` (know about ${known}): assuming type \`${
          field === 'quantity' ? 'number' : 'string'
        }\`.`
      );
    }

    const ty = ctx.inputType.get(field);

    if (ty === undefined) {
      if (field === 'quantity') {
        return { ty: 'number' };
      } else {
        return { ty: 'string' };
      }
    }

    if (ty.ty === 'quantity') {
      // This looks useless, but we're dropping an extra "column" field from
      // RowType
      return {
        ty: 'quantity',
        units: ty.units,
      };
    }
    return ty;
  },

  eval(ctx: Context, call: ASTCall): unknown {
    const field = extractStringLiteral(call.args[0]);

    const v = ctx.input.get(field);
    if (v === undefined) {
      throw new Error(`BART column \`${field}\` was not found.`);
    }

    return assertConstantValue(v);
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    const ty = typecheckExpression(ctx, call);
    const name = extractStringLiteral(call.args[0]);
    // Tags are a little bit weird. We only support string tags for now

    // [Hack 2023-03-18] Using struct_extract + json_transform instead of
    // the simpler json_extract_string because the latter doesn't treat
    // JSON nulls in a useful way.  Bug report filed.
    if (name.startsWith('tags.')) {
      const tag = name.substring(5);
      return traceHelper<TraceASTInput>(
        ty,
        {
          expr: `struct_extract(json_transform(${addInputVariable(
            ctx,
            'tags'
          )}, '{ "${tag}": "VARCHAR" }'), ${singleQuote(tag)})`,
          stage: 0,
          unitExpr: null,
          error: null,
        },
        () => ({
          t: TraceASTType.TInput,
          c: name,
        })
      );
    }

    // Note when type is quantity we get unit from the unit column
    const inputTy = ctx.inputType.get(name);
    const unitCol = inputTy?.ty === 'quantity' ? inputTy.column : undefined;

    return traceHelper<TraceASTInput>(
      ty,
      {
        expr: addInputVariable(ctx, name),
        stage: 0,
        unitExpr: unitCol ? addInputVariable(ctx, unitCol) : null,
        error: null,
      },
      () => ({
        t: TraceASTType.TInput,
        c: name,
      })
    );
  },
};
